import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.

export const vitamindCarlos: Scenario = {
  slug: 'vitamind-carlos',
  series: 'Nutrient Absorption',
  title: "He took vitamin D for two years. His level barely moved.",
  socialHook:
    "Carlos, 44: he took vitamin D every day for two years, but his blood level stayed low. The mystery was not consistency. It was conversion, transport, and response.",
  meta: {
    description:
      "A fictional case study: Carlos took vitamin D daily and still tested low. His VDR, GC, and CYP2R1 results explain why a standard dose can undershoot for him and what to discuss next.",
  },

  persona: {
    name: 'Carlos',
    age: 44,
    sex: 'Male',
    ancestry: 'Latin American',
    occupation: 'Architect',
    chiefConcern:
      "He has taken 2,000 IU of vitamin D every day for two years, but his latest level is still 19 ng/mL. He feels tired, his mood dips in winter, and he wants to know why the number will not move.",
    familyHistory: 'Father: osteoporosis diagnosed at age 68.',
  },

  clinicalPicture: {
    symptoms: [
      'Persistent fatigue, worse in winter',
      'Low mood in winter months',
      'No muscle weakness or bone pain currently',
    ],
    labs: [
      {
        name: 'Vitamin D (25-OHD)',
        value: '19 ng/mL',
        refRange: '20–50 ng/mL',
        flag: 'low',
      },
      {
        name: 'Bone density (lumbar spine)',
        value: 'T-score −1.3',
        refRange: 'T-score above −1.0',
        flag: 'low',
      },
    ],
    medications: ['No prescription medications'],
    supplements: ['Vitamin D3 2,000 IU daily (two years)'],
    lifestyle: [
      'Office-based work, limited outdoor time',
      'Diet: varied, not high in oily fish or fortified foods',
      'Non-smoker, alcohol occasional',
    ],
  },

  genetics: [
    {
      gene: 'VDR',
      variant: 'Taq1',
      rsid: 'rs731236',
      genotype: 'Reduced-sensitivity variant',
      plainMeaning:
        "Carlos's cells may need a higher vitamin D level than average before they get the same signal.",
      technicalDetail:
        'VDR is the receptor that vitamin D binds to inside cells — the lock that vitamin D must fit to have any biological effect. Carlos carries a reduced-sensitivity variant, meaning the receptor binds and activates less efficiently. Standard dosing targets were calibrated on average VDR sensitivity — not his. Higher circulating vitamin D is required to achieve the same cellular signal.',
    },
    {
      gene: 'GC',
      variant: 'rs4588 / rs7041',
      rsid: 'rs4588 / rs7041',
      genotype: 'Reduced binding protein function',
      plainMeaning:
        "His blood test may make his vitamin D status look better than it feels inside his cells, because less may be freely available to use.",
      technicalDetail:
        "GC encodes the vitamin D binding protein, which transports vitamin D through the bloodstream. Only the unbound 'free' fraction can reach cells and do biological work — the bound fraction is inactive. Carlos's variant reduces the amount of free, bioavailable vitamin D relative to his total measured level. Standard blood tests measure total 25-OHD, not the free fraction, so his result on paper represents less functional vitamin D than the number suggests.",
    },
    {
      gene: 'CYP2R1',
      variant: 'rs10741657',
      rsid: 'rs10741657',
      genotype: 'Reduced-function variant',
      plainMeaning:
        "Carlos may swallow the supplement, but his liver converts less of it into the storage form measured on the blood test.",
      technicalDetail:
        "CYP2R1 is the liver enzyme responsible for the first activation step — converting vitamin D from food or supplements into 25-OHD, the storage form measured in standard blood tests. A reduced-function variant means less conversion per unit supplemented. This directly explains why years of consistent dosing produced only a modest rise in his blood level.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: 'Two years. Every day. Still low.',
      body: "Carlos's family doctor flagged low vitamin D at a routine check two years ago. The advice was simple: take 2,000 IU daily. He did it consistently, every morning with breakfast. At the next annual test, his level was still below range. The answer was to take more, but nobody explained why the first plan had barely worked. Carlos started to wonder what the blood test was missing.",
    },
    {
      type: 'narrative',
      heading: "What the vitamin D test misses",
      body: "The standard vitamin D test measures the storage form in the blood. It does not show how much is free to reach cells, how well the body converted the supplement, or how strongly the cells respond. For many people, the total number is a useful guide. For Carlos, the same number may mean less usable vitamin D than it would for someone else.",
    },
    {
      type: 'reveal',
      heading: 'His cells need a stronger signal',
      body: "Carlos may need a higher blood level to get the same effect inside his cells. His VDR result suggests the vitamin D receptor is less responsive than average, so a low-normal result may still act low for him. The reference range is built around population averages. Carlos is not average on this pathway.",
    },
    {
      type: 'reveal',
      heading: 'The supplement had two more hurdles',
      body: "The receptor result explains why his target may need to be higher. The GC and CYP2R1 results explain why getting there has been hard. One affects how much vitamin D is freely available to cells. The other affects how much of the supplement becomes 25-OHD, the form measured on his lab report. He was not failing the plan. The plan was running into three bottlenecks at once.",
    },
    {
      type: 'action',
      heading: "Five things to discuss with his clinician",
      items: [
        "Discuss a higher target, not just getting above the lower cutoff. His VDR result means the same blood level may not create the same cell signal.",
        "Adjust the dose only with clinician supervision and recheck in about 3 months. Monitoring keeps the plan effective and helps avoid sustained high-dose risk.",
        "Ask about magnesium alongside vitamin D. Magnesium supports vitamin D activation, including the CYP2R1 step that may already be slower for him.",
        "Ask whether vitamin K2 (MK-7) makes sense with D3. Given his family history of osteoporosis and borderline bone density, calcium direction is part of the conversation.",
        "Ask whether a free vitamin D test is available. It measures the unbound fraction, which is the part most relevant to his GC result.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: "Why standard dosing missed Carlos",
      caption:
        "The blood number did not tell the whole story. This shows how far his functional vitamin D may sit below his effective target, and which changes help close the gap. The dial is not a disease risk score.",
      baselineLabel: 'Gap from optimal for his VDR variant (on 2,000 IU)',
      baseline: 68,
      levers: [
        {
          label: 'Clinician-guided higher dose',
          delta: -25,
          description:
            'A higher dose can help offset lower CYP2R1 activation efficiency, because more input can still produce more converted vitamin D.',
        },
        {
          label: 'Add magnesium support',
          delta: -10,
          description:
            "Magnesium is required for CYP2R1 to function. His low-normal level may add another bottleneck to the activation step.",
        },
        {
          label: 'Vitamin K2 for bone support',
          delta: -5,
          description:
            'K2 helps direct absorbed calcium toward bone, which is relevant given his osteopenia finding and family history of osteoporosis.',
        },
        {
          label: 'Consistent daily timing (with a meal)',
          delta: -8,
          description:
            'Vitamin D is fat-soluble. Taking it with the largest meal of the day improves absorption by 30–50% compared to taking it fasted — a simple change that compounds over months.',
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'vitamind-carlos',
    label: 'Explore your vitamin D genetics in Coach',
  },
}
